import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import image from "./assets/Eximpe-black.webp";
import { HiChevronDown, HiMenu, HiX } from "react-icons/hi";
import { trackNavigationEvent, NavigationClick } from '../../utlis/useAnalytics';

export const pathToPageName: Record<string, string> = {
  '/': 'Global Collection',
  '/about-eximpe': 'About Eximpe',
  '/contact-us': 'Contact Us',
  '/request-demo': 'Request Demo',
  '/global-trade-account': 'Global Trade',
  '/grievance-redressal-policy': 'Redressal',
  '/privacy-policy': 'Privacy Policy',
  '/terms-and-conditions': 'Terms And Conditions',
  '/social-links': 'Link Tree',
  '/landing': 'Signup Landing',
  '/swift-code-finder': 'Swift Code Finder',
  '/invoice-generator': 'Invoice Generator',
  '/hscode': 'HS Code Finder',
  '/blog': 'Blog'
};

function getCurrentPageName(): string {
  const path = window.location.pathname;
  return pathToPageName[path];
}

const Navbar = () => {
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);

  const lastScrollY = useRef(0);
  const aboutUsTimeoutRef = useRef<number | null>(null);
  const toolsTimeoutRef = useRef<number | null>(null);

  const btnClickLogin = () => {
    trackNavigationEvent(NavigationClick.ClickedLoginButton, {
      section: 'Navigation',
      page: getCurrentPageName(),
    });
  };

  const btnClickSignup = () => {
    trackNavigationEvent(NavigationClick.ClickedSignUpButton, {
      section: 'Navigation',
      page: getCurrentPageName(),
    });
  };

  const defaultUTM =
    '?utm_source=online&utm_medium=website&utm_campaign=website_lead&utm_term=website_visitor';

  const [utmParams, setUtmParams] = useState<string>(() => {
    return localStorage.getItem('utm_params') || defaultUTM;
  });

  useEffect(() => {
    const queryString = window.location.search;
    if (queryString && queryString.includes('utm_')) {
      localStorage.setItem('utm_params', queryString);
      setUtmParams(queryString);
    }
  }, []);

  const signupLink = `https://app.eximpe.com/auth/sign-up${utmParams}`;
  const loginLink = `https://app.eximpe.com/auth/login${utmParams}`;

  // Scroll to top helper
  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  // Close mobile menu when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsAboutUsOpen(false);
        setIsToolsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigation = [
    { name: "Global Collection Account", href: "/" },
    { name: "Global Trade Account", href: "/global-trade-account" },
    // { name: "Financing", href: "/financing" },
    { name: "Blog", href: "/blog" },
    {
      name: "Tools",
      href: "",
      submenu: [
        { name: "Swift Code Finder", href: "/swift-code-finder" },
        { name: "HS Code Finder", href: "/hscode" },
        { name: "Invoice Generator", href: "/invoice-generator" },
      ],
    },
    {
      name: "About Us",
      href: "",
      submenu: [
        { name: "About EximPe", href: "/about-eximpe" },
        { name: "Contact Us", href: "/contact-us" },
        { name: "Request Demo", href: "/request-demo" },
      ],
    },
  ];

  const handleAboutUsMouseEnter = () => {
    if (aboutUsTimeoutRef.current !== null) {
      clearTimeout(aboutUsTimeoutRef.current);
    }
    setIsAboutUsOpen(true);
  };

  const handleAboutUsMouseLeave = () => {
    aboutUsTimeoutRef.current = window.setTimeout(() => {
      setIsAboutUsOpen(false);
    }, 150);
  };

  const handleToolsMouseEnter = () => {
    if (toolsTimeoutRef.current !== null) {
      clearTimeout(toolsTimeoutRef.current);
    }
    setIsToolsOpen(true);
  };

  const handleToolsMouseLeave = () => {
    toolsTimeoutRef.current = window.setTimeout(() => {
      setIsToolsOpen(false);
    }, 150);
  };

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const closeMobileMenu = () => {
    setIsOpen(false);
    setIsAboutUsOpen(false);
    setIsToolsOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-lg transition-transform duration-300 w-full ${
          showNavbar ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0 flex items-center hover:scale-[105%] transition-transform duration-300 w-[120px]">
              <Link to="/" onClick={scrollToTop}>
                <img src={image} alt="Eximpe Logo" className="w-full h-auto" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navigation.map((item) =>
                item.submenu ? (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={
                      item.name === "About Us"
                        ? handleAboutUsMouseEnter
                        : item.name === "Tools"
                        ? handleToolsMouseEnter
                        : undefined
                    }
                    onMouseLeave={
                      item.name === "About Us"
                        ? handleAboutUsMouseLeave
                        : item.name === "Tools"
                        ? handleToolsMouseLeave
                        : undefined
                    }
                  >
                    <div
                      className={`cursor-pointer px-2 text-[16px] py-2 mt-[2px] flex items-center whitespace-nowrap text-sm transition-colors duration-200 ${
                        item.submenu.some((subItem) => isActive(subItem.href))
                          ? "text-[#26a887] font-medium"
                          : "text-gray-700 hover:text-[#26a887]"
                      }`}
                    >
                      {item.name}
                      <HiChevronDown className="ml-1 transition-transform duration-200" />
                    </div>
                    {((isAboutUsOpen && item.name === "About Us") ||
                      (isToolsOpen && item.name === "Tools")) && (
                      <div className="absolute top-10 left-0 bg-white shadow-lg py-2 rounded-lg w-48 border border-gray-100 animate-in fade-in slide-in-from-top-2 duration-200">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={scrollToTop}
                            className={`block px-4 py-2 text-sm transition-colors duration-150 ${
                              isActive(subItem.href)
                                ? "text-[#26a887] font-medium bg-green-50"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={scrollToTop}
                    className={`px-2 py-2 text-[16px] whitespace-nowrap text-sm transition-colors duration-200 ${
                      isActive(item.href)
                        ? "text-[#26a887] font-medium"
                        : "text-gray-700 hover:text-[#26a887]"
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </div>

            {/* Desktop Auth Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <a href={loginLink} onClick={scrollToTop}>
                <button 
                  onClick={btnClickLogin} 
                  className="px-4 py-2 text-gray-700 hover:cursor-pointer hover:text-[#26a887] transition-colors duration-200"
                >
                  Login
                </button>
              </a>
              <a href={signupLink}>
                <button 
                  onClick={btnClickSignup} 
                  className="px-4 py-2 hover:cursor-pointer bg-[#26a887] text-white rounded-lg hover:bg-[#034435] transition-colors duration-200"
                >
                  Sign Up
                </button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#26a887] hover:bg-gray-100 focus:outline-none transition-colors duration-200"
              >
                <HiMenu
                  className={`${
                    isOpen ? "hidden" : "block"
                  } h-6 w-6 transition-opacity duration-200`}
                  aria-hidden="true"
                />
                <HiX
                  className={`${
                    isOpen ? "block" : "hidden"
                  } h-6 w-6 transition-opacity duration-200`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/70"
          onClick={closeMobileMenu}
        />
        
        {/* Mobile Menu Panel */}
        <div
          className={`absolute top-20 left-0 right-0 bg-white shadow-xl transition-all duration-300 ease-out transform ${
            isOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-6 max-h-[calc(100vh-5rem)] overflow-y-auto">
            {/* Navigation Items */}
            <div className="space-y-3">
              {navigation.map((item) => (
                <div key={item.name} className="mb-2">
                  {item.submenu ? (
                    <div>
                      <button
                        onClick={() => {
                          if (item.name === "About Us") setIsAboutUsOpen(!isAboutUsOpen);
                          if (item.name === "Tools") setIsToolsOpen(!isToolsOpen);
                        }}
                        className={`w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg flex items-center justify-between transition-colors duration-200 ${
                          item.submenu.some((subItem) => isActive(subItem.href))
                            ? "text-[#26a887] bg-green-50"
                            : ""
                        }`}
                      >
                        <span className="font-medium">{item.name}</span>
                        <HiChevronDown
                          className={`w-5 h-5 transition-transform duration-200 ${
                            (isAboutUsOpen && item.name === "About Us") ||
                            (isToolsOpen && item.name === "Tools")
                              ? "transform rotate-180"
                              : ""
                          }`} 
                        />
                      </button>
                      
                      {/* Submenu with animation */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-out ${
                          (isAboutUsOpen && item.name === "About Us") || 
                          (isToolsOpen && item.name === "Tools")
                            ? "max-h-96 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="pl-4 space-y-1 py-2">
                          {item.submenu.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => {
                                scrollToTop();
                                closeMobileMenu();
                              }}
                              className={`block px-4 py-2.5 text-sm rounded-lg transition-colors duration-200 ${
                                isActive(subItem.href)
                                  ? "text-[#26a887] font-medium bg-green-50"
                                  : "text-gray-600 hover:bg-gray-50"
                              }`}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => {
                        scrollToTop();
                        closeMobileMenu();
                      }}
                      className={`block px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                        isActive(item.href)
                          ? "text-[#26a887] bg-green-50"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Auth Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-200 space-y-4">
              <a 
                href={loginLink} 
                onClick={() => { scrollToTop(); closeMobileMenu(); }}
                className="block"
              >
                <button 
                  onClick={btnClickLogin} 
                  className="w-full px-6 py-3 text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-lg font-medium transition-all duration-200 hover:border-gray-400 text-center"
                >
                  Login
                </button>
              </a>
              <a 
                href={signupLink} 
                onClick={() => { scrollToTop(); closeMobileMenu(); }}
                className="block"
              >
                <button 
                  onClick={btnClickSignup} 
                  className="w-full px-6 py-3 bg-[#26a887] text-white rounded-lg hover:bg-[#034435] font-medium transition-all duration-200 shadow-sm hover:shadow-md text-center"
                >
                  Sign Up
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;